import { jsx as _jsx } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
const getBadgeClasses = cva('inline-flex items-center rounded-full font-sans font-medium', {
    variants: {
        size: {
            sm: 'px-1.5 py-three text-[11px]',
            lg: 'px-2 py-1 text-sm',
        },
        variant: {
            neutral: 'bg-soft text-secondary',
            info: 'bg-super-soft text-super-primary',
            success: 'bg-positive-soft text-positive-primary',
            warning: 'bg-warning-soft text-warning-primary',
            error: 'bg-negative-soft text-negative-primary',
        },
    },
    defaultVariants: {
        variant: 'neutral',
        size: 'sm',
    },
});
/**
 * Inline status indicator using semantic color tokens. Supports neutral and semantic variants
 * matching Banner color tokens.
 *
 * @category components
 * @keywords badge, components, inline, status, indicator, using, semantic, color, tokens, supports,
 *   neutral, variants, matching, banner, chip, button, icon
 * @whenToUse Use Badge to display short, non-interactive status labels inline with other content.
 *   Badges are ideal for indicating item states (active, pending, failed), categories, counts, or
 *   feature tags. They use semantic color variants aligned with Banner (info, success, warning,
 *   error) plus a neutral option for non-semantic labels. Use them in tables, lists, headings, or
 *   anywhere concise visual status indicators are needed.
 * @guidelineDo
 *   - Use the semantic variant (info, success, warning, error) that matches the status meaning —
 *     info for informational, success for active/complete, warning for attention-needed, error for
 *     failed/critical
 *   - Use size="lg" for prominent panel-header statuses; the compact size="sm" remains the default
 *   - Keep badge text short — 1 to 3 words maximum — to maintain the compact, inline visual pattern
 *   - Use the neutral variant for non-semantic labels like counts, categories, or tags that don't
 *     convey status
 *   - Place badges inline with headings, table cells, or list items where they provide at-a-glance
 *     status context
 * @guidelineDont
 *   - Don't use badges for interactive elements — they are display-only status indicators, not
 *     buttons or links
 *   - Don't use long text in badges — truncation is not supported, so verbose labels will break the
 *     layout
 *   - Don't rely solely on badge color to communicate meaning — always include descriptive text
 *     content
 *   - Don't use badges for numeric counts without context — pair with a label or place in a context
 *     where the count meaning is clear
 *   - Don't use a disabled Button or a Chip as a status label — Badge is the correct component for
 *     read-only metadata and status indicators
 * @accessibility
 *   - Badge renders as a <span> element, making it semantically inline text — screen readers
 *     announce it as part of the surrounding text flow
 *   - The semantic variant colors (info=blue, success=green, warning=yellow, error=red) pair text
 *     color with background opacity for sufficient contrast in both light and dark modes
 *   - Badge does not include any ARIA role — when the badge communicates a live status update, wrap
 *     it in an element with role='status' or aria-live
 *   - Badge keeps its label vertically aligned within the pill without changing the accessible text
 *     content
 * @relatedComponents
 *   - banner
 *   - chip
 *   - button
 *   - icon
 */
export function Badge({ children, variant, size }) {
    return (_jsx("span", { className: getBadgeClasses({ variant, size }), children: _jsx("span", { className: "inline-flex leading-none text-box-trim-both", children: children }) }));
}
//# sourceMappingURL=index.js.map